/**
 * Audit-log search & export-task list hook (Issue #287).
 *
 * Wraps the typed `@jinzhuan/api-client` GET calls so the `/settings`
 * audit-log search section can render real backend data.
 *
 * Red lines (mirror backend AuditLogController):
 *   - All data comes through `@jinzhuan/api-client` typed client.
 *   - The list / summary slices fire only when {@link canReadAuditLogs}
 *     evaluates true for the caller. The component layer must additionally
 *     gate the hook itself so unauthorized accounts never instantiate it.
 *   - Filters are forwarded as-is; the backend rejects malformed values.
 *     The frontend does not echo raw payload, full IP, or full user agent —
 *     those fields are not returned by the API.
 *   - Pagination uses the backend opaque `nextCursor` (preferred) and falls
 *     back to `offset` when the caller is on page 0. The hook never tries to
 *     pull all rows.
 *   - Export task list is read-only. Export creation requires a real
 *     second-factor flow; this hook never POSTs `secondFactorResult=passed`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createApiClient,
  type AuditLogEntryDto,
  type AuditLogExportDto,
  type AuditLogListQuery,
  type AuditLogListResponse,
  type AuditLogSummaryQuery,
  type AuditLogSummaryResponse,
} from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';
import {
  canReadAuditLogExports,
  canReadAuditLogs,
} from '@/lib/auditLogPermissions';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export interface AsyncState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: string | null;
}

function pickErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const maybeMessage = (err as { message?: string }).message;
    if (typeof maybeMessage === 'string') return maybeMessage;
  }
  return fallback;
}

/**
 * Subset of {@link AuditLogListQuery} the UI form drives. Pagination state
 * (`offset` / `cursor`) is managed separately so callers cannot accidentally
 * mix filter changes with pagination state and produce inconsistent pages.
 */
export type AuditLogSearchFilters = Omit<
  AuditLogListQuery,
  'limit' | 'offset' | 'cursor'
>;

export const DEFAULT_AUDIT_LOG_PAGE_SIZE = 25;
export const MAX_AUDIT_LOG_PAGE_SIZE = 100;

export interface UseAuditLogsOptions {
  readonly permissions: readonly string[];
  readonly pageSize?: number;
}

export interface UseAuditLogsReturn {
  readonly canRead: boolean;
  readonly canReadExports: boolean;
  readonly filters: AuditLogSearchFilters;
  readonly limit: number;
  readonly list: AsyncState<AuditLogListResponse>;
  readonly rows: readonly AuditLogEntryDto[];
  readonly summary: AsyncState<AuditLogSummaryResponse>;
  readonly exports: AsyncState<readonly AuditLogExportDto[]>;
  readonly hasNextPage: boolean;
  readonly hasPreviousPage: boolean;
  readonly currentPageIndex: number;
  applyFilters: (next: AuditLogSearchFilters) => void;
  resetFilters: () => void;
  nextPage: () => void;
  previousPage: () => void;
  refresh: () => Promise<void>;
}

function normaliseFilters(input: AuditLogSearchFilters): AuditLogSearchFilters {
  const out: Record<string, string | undefined> = {};
  for (const [key, value] of Object.entries(input)) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed.length === 0) continue;
    out[key] = trimmed;
  }
  return out as AuditLogSearchFilters;
}

function clampPageSize(raw: number | undefined): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw <= 0) {
    return DEFAULT_AUDIT_LOG_PAGE_SIZE;
  }
  return Math.min(Math.floor(raw), MAX_AUDIT_LOG_PAGE_SIZE);
}

export function useAuditLogs(opts: UseAuditLogsOptions): UseAuditLogsReturn {
  const { permissions } = opts;
  const canRead = canReadAuditLogs(permissions);
  const canReadExports = canReadAuditLogExports(permissions);
  const limit = clampPageSize(opts.pageSize);

  const [filters, setFilters] = useState<AuditLogSearchFilters>({});
  const [cursorStack, setCursorStack] = useState<readonly (string | null)[]>([
    null,
  ]);
  const [list, setList] = useState<AsyncState<AuditLogListResponse>>({
    data: null,
    loading: false,
    error: null,
  });
  const [summary, setSummary] = useState<AsyncState<AuditLogSummaryResponse>>({
    data: null,
    loading: false,
    error: null,
  });
  const [exports, setExports] = useState<AsyncState<readonly AuditLogExportDto[]>>({
    data: null,
    loading: false,
    error: null,
  });

  const requestSeqRef = useRef(0);

  const currentPageIndex = cursorStack.length - 1;
  const activeCursor = cursorStack[currentPageIndex] ?? null;

  const loadList = useCallback(
    async (seq: number) => {
      if (!canRead) return;
      setList((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const query: AuditLogListQuery = activeCursor
          ? { ...filters, limit, cursor: activeCursor }
          : { ...filters, limit, offset: 0 };
        const { data, error } = await apiClient.GET('/api/v1/audit-logs', {
          params: { query },
        });
        if (seq !== requestSeqRef.current) return;
        if (error) {
          setList({
            data: null,
            loading: false,
            error: pickErrorMessage(error, '加载审计日志失败'),
          });
          return;
        }
        setList({ data: data ?? null, loading: false, error: null });
      } catch (err) {
        if (seq !== requestSeqRef.current) return;
        setList({
          data: null,
          loading: false,
          error: pickErrorMessage(err, '加载审计日志失败'),
        });
      }
    },
    [canRead, filters, limit, activeCursor],
  );

  const loadSummary = useCallback(
    async (seq: number) => {
      if (!canRead) return;
      setSummary((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const summaryQuery: AuditLogSummaryQuery = { ...filters };
        const { data, error } = await apiClient.GET(
          '/api/v1/audit-logs/summary',
          { params: { query: summaryQuery } },
        );
        if (seq !== requestSeqRef.current) return;
        if (error) {
          setSummary({
            data: null,
            loading: false,
            error: pickErrorMessage(error, '加载审计聚合摘要失败'),
          });
          return;
        }
        setSummary({ data: data ?? null, loading: false, error: null });
      } catch (err) {
        if (seq !== requestSeqRef.current) return;
        setSummary({
          data: null,
          loading: false,
          error: pickErrorMessage(err, '加载审计聚合摘要失败'),
        });
      }
    },
    [canRead, filters],
  );

  const loadExports = useCallback(
    async (seq: number) => {
      if (!canReadExports) return;
      setExports((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const { data, error } = await apiClient.GET(
          '/api/v1/audit-logs/exports',
          { params: { query: { limit: 20 } } },
        );
        if (seq !== requestSeqRef.current) return;
        if (error) {
          setExports({
            data: null,
            loading: false,
            error: pickErrorMessage(error, '加载审计导出任务失败'),
          });
          return;
        }
        setExports({
          data: data?.rows ?? [],
          loading: false,
          error: null,
        });
      } catch (err) {
        if (seq !== requestSeqRef.current) return;
        setExports({
          data: null,
          loading: false,
          error: pickErrorMessage(err, '加载审计导出任务失败'),
        });
      }
    },
    [canReadExports],
  );

  const refresh = useCallback(async () => {
    requestSeqRef.current += 1;
    const seq = requestSeqRef.current;
    await Promise.all([loadList(seq), loadSummary(seq), loadExports(seq)]);
  }, [loadList, loadSummary, loadExports]);

  useEffect(() => {
    if (!canRead) return;
    void refresh();
  }, [canRead, refresh]);

  const applyFilters = useCallback((next: AuditLogSearchFilters) => {
    setFilters(normaliseFilters(next));
    setCursorStack([null]);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({});
    setCursorStack([null]);
  }, []);

  const nextPage = useCallback(() => {
    setList((prev) => prev);
    setCursorStack((prev) => {
      const last = prev[prev.length - 1];
      const next = list.data?.nextCursor ?? null;
      if (!next) return prev;
      if (next === last) return prev;
      return [...prev, next];
    });
  }, [list.data?.nextCursor]);

  const previousPage = useCallback(() => {
    setCursorStack((prev) => {
      if (prev.length <= 1) return prev;
      return prev.slice(0, -1);
    });
  }, []);

  const rows = useMemo(() => list.data?.rows ?? [], [list.data]);
  const hasNextPage = Boolean(list.data?.nextCursor);
  const hasPreviousPage = cursorStack.length > 1;

  return {
    canRead,
    canReadExports,
    filters,
    limit,
    list,
    rows,
    summary,
    exports,
    hasNextPage,
    hasPreviousPage,
    currentPageIndex,
    applyFilters,
    resetFilters,
    nextPage,
    previousPage,
    refresh,
  };
}

export type {
  AuditLogEntryDto,
  AuditLogExportDto,
  AuditLogListResponse,
  AuditLogSummaryResponse,
};
